import type { AudienceEntry } from '@tmh-website/services/audience.service';
import { Audience, Project } from '@tmh-website/shared/const';

// noinspection ES6PreferShortImport
import type { LogoComponent, LogoId } from './useLogoComponent.contracts';
import { LOGO_ID_TO_COMPONENT_MAP, LOGO_IDS } from './useLogoComponent.config';

/**
 * Resolves the applicable logo component based on the passed-in `project`.
 *
 * @param project - The project which logo we want to resolve for.
 * @param [logoId=LOGO_IDS.Default] - Optional, custom logo ID (to override the default project logotype).
 *
 * @returns - The applicable logo component for the current project.
 *
 * @throws Error - When failed to resolve the `LogoComponent` associated with the specified `LogoId`.
 */
export const getLogoComponentByProject = (project: Project, logoId: LogoId = LOGO_IDS.Default): LogoComponent => {
  switch (project) {
    case Project.Energy: {
      return getLogoComponentById(LOGO_IDS.Energy);
    }

    case Project.Website: {
      if (typeof logoId === 'undefined') {
        throw new Error(`The project '${Project.Website}' requires the 'logoId' to be specified!`);
      }

      return getLogoComponentById(logoId);
    }

    default: {
      return getLogoComponentById(LOGO_IDS.Default);
    }
  }
};

/**
 * Resolves the applicable logo component based on the passed-in `LogoId`.
 *
 * @param logoId - The `LogoId` which associated `LogoComponent` we want to resolve.
 *
 * @throws Error - When failed to resolve the `LogoComponent` associated with the pass-in `LogoId`.
 */
export const getLogoComponentById = (logoId: LogoId): LogoComponent => {
  const logoComponent: LogoComponent = LOGO_ID_TO_COMPONENT_MAP[logoId];

  if (typeof logoComponent === 'undefined') {
    throw new Error(`Failed to resolve the 'LogoComponent' for the 'LogoId' of '${logoId}'!`);
  }

  return logoComponent;
};

/**
 * Resolves the `LogoId` associated with the given audience.
 *
 * @param [audience=Audience.Default] - The audience which `LogoId` we want to resolve.
 * @param [audienceEntries=[]] - The list of audiences to search in.
 *
 * @throws Error - When failed to resolve the `AudienceEntry` associated with the currently-active audience.
 */
export const getLogoIdByAudience = (
  audience: Audience = Audience.Default,
  audienceEntries: AudienceEntry[] = []
): LogoId => {
  const foundAudienceEntry: AudienceEntry | undefined = audienceEntries.find(entry => entry.key === audience);

  // If the audiences ARE configured inside the Strapi CMS, but there's still no matching `AudienceEntry` for the
  // currently-active `Audience`, we're throwing an error, 'cause this would be rather an unexpected situation.
  // The only special case is `Audience.Default`, 'cause there are some pages that will always have this audience
  // selected, even if it's not configured inside the Strapi CMS, so we should handle this special case, too.
  if (typeof foundAudienceEntry === 'undefined') {
    if (audience === Audience.Default) {
      return LOGO_IDS.Default;
    } else {
      throw new Error(`Failed to resolve the 'AudienceEntry' for the audience '${audience}'!`);
    }
  }

  return foundAudienceEntry.logo as LogoId;
};
